import { useEffect, useMemo, useRef, useState } from "react";
import { useIntegrations } from "../lib/integrations";
import { toast } from "../components/Toast";
import { DateInput } from "../components/DateInput";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useOrg } from "../lib/session";
import { useWords } from "../lib/workspace";
import { money } from "../lib/plans";
import { contactName } from "../lib/format";
import type { Appointment, BookingResource, BookingService, BookingSetup, Contact } from "../lib/types";
import { ErrorText, Field, Loading, PageHeader } from "../components/ui";
import { CalendarConnections, resultText } from "../components/CalendarConnections";
import { registerWarmer } from "../lib/smooth";
import { addDays, dayKey, fmtDay, fmtTime, minutesLabel, startOf, wall, weekdayOf, zoned } from "../lib/tz";

const setupQuery = (orgId: string, base: string) => ({ queryKey: [orgId, "booking-setup"], queryFn: () => api<BookingSetup>(`${base}/bookings/setup`) });
registerWarmer("calendar", (qc, { orgId, base }) => { qc.prefetchQuery({ ...setupQuery(orgId, base), staleTime: 30_000 }); });

const PX_PER_MIN = 1.1; // 66px per hour

const statusText: Record<Appointment["status"], string> = {
  booked: "Booked",
  confirmed: "Confirmed",
  completed: "Attended",
  no_show: "No-show",
  cancelled: "Cancelled",
};

function weekStart(key: string): string {
  return addDays(key, -weekdayOf(key)); // weeks start on Sunday
}

/** Opening windows (minutes) for a day: a resource's own hours or the business's. */
function windowsFor(setup: BookingSetup, weekday: number, resource: string | null): [number, number][] {
  const own = resource ? setup.hours.filter((h) => h.resource_id === resource) : [];
  const rows = own.length > 0 ? own : setup.hours.filter((h) => h.resource_id === null);
  return rows.filter((h) => h.weekday === weekday).map((h) => [h.start_minute, h.end_minute]);
}

/** Working windows for anyone in a list (merged), for "anyone available" and "Everyone". */
function unionWindows(setup: BookingSetup, weekday: number, ids: (string | null)[]): [number, number][] {
  const all = ids.flatMap((id) => windowsFor(setup, weekday, id)).sort((a, b) => a[0] - b[0]);
  const out: [number, number][] = [];
  for (const [a, b] of all) {
    const last = out[out.length - 1];
    if (last && a <= last[1]) last[1] = Math.max(last[1], b);
    else out.push([a, b]);
  }
  return out;
}

type SlotState = "free" | "busy" | "outside";

/**
 * SlotPicker shows a day's start times for one person (or anyone): free ones
 * to pick, booked or unavailable ones dimmed in red, and, only when asked
 * for, times outside working hours.
 */
function SlotPicker({ tz, setup, duration, date, resourceIds, resourceName, exceptId, serviceId, resourceId, value, onChange, outside, onOutside }: {
  tz: string; setup: BookingSetup; duration: number; date: string; resourceIds: string[]; resourceName: string | null; exceptId?: string;
  serviceId: string; resourceId: string; value: string; onChange: (time: string, outside: boolean) => void; outside: boolean; onOutside: (v: boolean) => void;
}) {
  const { base } = useOrg();
  const avail = useQuery({
    queryKey: ["avail", base, serviceId, date, resourceId, exceptId ?? ""],
    queryFn: () => api<{ days: { date: string; slots: { start: string; resource_ids: string[] }[] }[] }>(
      `${base}/bookings/availability?service_id=${serviceId}&from=${date}&days=1${resourceId ? `&resource_id=${resourceId}` : ""}${exceptId ? `&except=${exceptId}` : ""}`),
    enabled: !!serviceId && !!date,
  });
  const step = setup.settings.slot_interval_minutes || 15;
  const wd = weekdayOf(date);
  const windows = resourceIds.length ? unionWindows(setup, wd, resourceIds) : windowsFor(setup, wd, null);
  const free = new Set((avail.data?.days[0]?.slots ?? []).map((sl) => fmtTime(new Date(sl.start), tz)));
  const inHours = (m: number) => windows.some(([a, b]) => m >= a && m + duration <= b);
  const lo = windows.length ? Math.min(...windows.map((w) => w[0])) : 9 * 60;
  const hi = windows.length ? Math.max(...windows.map((w) => w[1])) : 17 * 60;
  const from = outside ? Math.max(0, Math.min(lo, 7 * 60) - 120) : lo;
  const to = outside ? Math.min(24 * 60, Math.max(hi, 20 * 60) + 120) : hi;
  const times: { label: string; state: SlotState }[] = [];
  for (let m = Math.ceil(from / step) * step; m + Math.min(duration, step) <= to; m += step) {
    const label = minutesLabel(m);
    const state: SlotState = free.has(label) ? "free" : inHours(m) ? "busy" : "outside";
    if (state === "outside" && !outside) continue;
    times.push({ label, state });
  }
  const freeCount = times.filter((t) => t.state === "free").length;
  const who = resourceName ?? "anyone";
  return (
    <div className="slot-picker">
      <div className="slot-legend small">
        <span><i className="sw free" /> Free</span>
        <span><i className="sw busy" /> Booked or unavailable</span>
        {outside && <span><i className="sw outside" /> Outside working hours</span>}
        <span className="muted">{avail.isLoading ? "Checking…" : windows.length === 0 ? `${resourceName ?? "Nobody"} works that day` : `Working ${windows.map(([a, b]) => `${minutesLabel(a)}–${minutesLabel(b)}`).join(", ")}`}</span>
      </div>
      {!avail.isLoading && windows.length === 0 && !outside && <p className="small muted">{resourceName ? `${resourceName} doesn't work on this day.` : "Nobody works on this day."} Pick another date, or show times outside working hours.</p>}
      {!avail.isLoading && windows.length > 0 && freeCount === 0 && <p className="small muted">No free times with {who} on this day.</p>}
      <div className="slot-chips" role="group" aria-label="Start time">
        {times.map((t) => (
          <button key={t.label} type="button" disabled={t.state === "busy"}
            className={`slot ${t.state}${t.label === value ? " on" : ""}`}
            title={t.state === "busy" ? "Booked or unavailable" : t.state === "outside" ? `Outside ${resourceName ? resourceName + "'s" : ""} working hours` : "Free"}
            onClick={() => onChange(t.label, t.state === "outside")}>{t.label}</button>
        ))}
      </div>
      <label className="check small"><input type="checkbox" checked={outside} onChange={(e) => onOutside(e.target.checked)} /> Show times outside working hours</label>
      {value && !free.has(value) && outside && <p className="small warn-text">{value} is outside {resourceName ? `${resourceName}'s` : "working"} hours. It will be booked anyway; the customer won't be told it's unusual.</p>}
    </div>
  );
}

interface Placed {
  a: Appointment;
  top: number;
  height: number;
  lane: number;
  lanes: number;
}

/** layout gives overlapping appointments side-by-side lanes. */
function layout(list: Appointment[], tz: string, gridStart: number): Placed[] {
  const items = list
    .map((a) => {
      const s = wall(new Date(a.starts_at), tz);
      const e = new Date(a.ends_at).getTime() - new Date(a.starts_at).getTime();
      const start = s.h * 60 + s.min;
      return { a, start, end: start + e / 60000 };
    })
    .sort((x, y) => x.start - y.start || x.end - y.end);
  const out: Placed[] = [];
  let cluster: { item: (typeof items)[number]; lane: number }[] = [];
  let clusterEnd = -1;
  const flush = () => {
    const lanes = Math.max(1, ...cluster.map((c) => c.lane + 1));
    for (const c of cluster) {
      out.push({ a: c.item.a, top: (c.item.start - gridStart) * PX_PER_MIN, height: Math.max(22, (c.item.end - c.item.start) * PX_PER_MIN - 2), lane: c.lane, lanes });
    }
    cluster = [];
  };
  for (const it of items) {
    if (it.start >= clusterEnd && cluster.length) flush();
    const used = new Set(cluster.filter((c) => c.item.end > it.start).map((c) => c.lane));
    let lane = 0;
    while (used.has(lane)) lane++;
    cluster.push({ item: it, lane });
    clusterEnd = Math.max(clusterEnd, it.end);
  }
  if (cluster.length) flush();
  return out;
}

export default function Calendar() {
  const { base, orgId, allows } = useOrg();
  const w = useWords();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const setup = useQuery(setupQuery(orgId, base));
  const integ = useIntegrations();
  const tz = setup.data?.settings.timezone ?? "Asia/Bahrain";
  const today = dayKey(new Date(), tz);
  const [anchor, setAnchor] = useState<string | null>(null);
  const [view, setView] = useState<"week" | "day">(() => (typeof window !== "undefined" && window.innerWidth < 760 ? "day" : "week"));
  const [filter, setFilter] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ day: string; minute: number | null; resourceId: string | null } | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { setDraft(null); setSelected(null); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const day0 = anchor ?? today;
  const days = view === "week" ? Array.from({ length: 7 }, (_, i) => addDays(weekStart(day0), i)) : [day0];
  const from = startOf(days[0], tz);
  const to = startOf(addDays(days[days.length - 1], 1), tz);
  const apptKey = [orgId, "appointments", from.toISOString(), to.toISOString()];
  const appts = useQuery({
    queryKey: apptKey,
    queryFn: () => api<{ appointments: Appointment[] }>(`${base}/appointments?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`),
    enabled: !!setup.data,
    refetchInterval: 30_000,
  });
  const busyQ = useQuery({
    queryKey: [orgId, "calendar-busy", from.toISOString(), to.toISOString()],
    queryFn: () => api<{ busy: { resource_id: string; start: string; end: string; provider: string }[] }>(`${base}/calendar/busy?from=${encodeURIComponent(from.toISOString())}&to=${encodeURIComponent(to.toISOString())}`),
    enabled: !!setup.data,
    refetchInterval: 120_000,
  });
  const [syncOpen, setSyncOpen] = useState(false);
  const [calResult] = useState(() => params.get("calendar"));
  useEffect(() => {
    if (calResult) { setSyncOpen(true); params.delete("calendar"); setParams(params, { replace: true }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // ?sync=1 (from the sidebar search) opens "Connect Google or Outlook calendar".
  useEffect(() => {
    if (params.has("sync")) { setSyncOpen(true); params.delete("sync"); setParams(params, { replace: true }); }
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => {
    qc.invalidateQueries({ queryKey: [orgId, "appointments"] });
    qc.invalidateQueries({ queryKey: [orgId, "deals"] });
  };

  // Opened from an event on Google or Outlook: show that booking.
  useEffect(() => {
    const appt = params.get("appointment"), date = params.get("date");
    if (!appt) return;
    if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) { setAnchor(date); setView("day"); }
    setSelected(appt);
    params.delete("appointment"); params.delete("date"); setParams(params, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Opened from a contact page: start booking for that customer.
  const bookFor = params.get("contact");
  useEffect(() => {
    if (bookFor && setup.data) setDraft({ day: today, minute: null, resourceId: null });
  }, [bookFor, setup.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const scroller = useRef<HTMLDivElement>(null);
  const scrolledFor = useRef("");
  const [drag, setDrag] = useState<{ id: string; x0: number; y0: number; dx: number; dy: number } | null>(null);
  const [moveError, setMoveError] = useState<unknown>(null);
  const justDragged = useRef(false);
  const openAtRef = useRef(0);
  // Open at the start of the working day, once per week or day shown.
  const viewKey = `${view}:${day0}`;
  useEffect(() => {
    const el = scroller.current;
    if (!el || !setup.data || scrolledFor.current === viewKey) return;
    scrolledFor.current = viewKey;
    el.scrollTop = openAtRef.current * PX_PER_MIN;
  });
  if (setup.isLoading) return <Loading />;
  if (setup.error) return <ErrorText error={setup.error} />;
  const s = setup.data!;
  const resources = s.resources.filter((r) => r.active);
  const services = s.services.filter((x) => x.active);
  const list = (appts.data?.appointments ?? []).filter((a) => !filter || a.resource_id === filter);

  // Grid hours: opening hours, widened to fit any appointment.
  let openAt = 8 * 60;
  let gridStart = 8 * 60;
  let gridEnd = 20 * 60;
  if (s.hours.length) {
    gridStart = Math.min(...s.hours.map((h) => h.start_minute));
    gridEnd = Math.max(...s.hours.map((h) => h.end_minute));
  }
  for (const a of list) {
    const st = wall(new Date(a.starts_at), tz);
    const en = wall(new Date(a.ends_at), tz);
    gridStart = Math.min(gridStart, st.h * 60);
    gridEnd = Math.max(gridEnd, en.h * 60 + en.min);
  }
  // Busy times from Google or Outlook outside working hours still show.
  for (const b of busyQ.data?.busy ?? []) {
    if (filter && b.resource_id !== filter) continue;
    const sd = new Date(b.start), ed = new Date(b.end);
    if (ed.getTime() - sd.getTime() >= 20 * 3600_000) continue; // all-day: shown across the hours already there
    const st = wall(sd, tz), en = wall(ed, tz);
    gridStart = Math.min(gridStart, st.h * 60);
    gridEnd = Math.max(gridEnd, en.h === 0 && en.min === 0 ? 24 * 60 : en.h * 60 + en.min);
  }
  // The whole day is there to scroll to; the view opens at opening time.
  openAt = Math.max(0, Math.floor(gridStart / 60) * 60 - 30);
  gridStart = 0;
  gridEnd = 24 * 60;
  openAtRef.current = openAt;
  const height = (gridEnd - gridStart) * PX_PER_MIN;
  const hours = Array.from({ length: (gridEnd - gridStart) / 60 }, (_, i) => gridStart + i * 60);
  const nowWall = wall(new Date(), tz);
  const nowMin = nowWall.h * 60 + nowWall.min;
  const sel = list.find((a) => a.id === selected) ?? null;
  const emptySetup = services.length === 0 || resources.length === 0;

  const title = view === "week"
    ? `${fmtDay(days[0], { day: "numeric", month: "short" })} – ${fmtDay(days[6], { day: "numeric", month: "short", year: "numeric" })}`
    : fmtDay(days[0], { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  return (
    <>
      <PageHeader
        title="Calendar"
        actions={
          <>
            {(integ.has("google_calendar") || integ.has("outlook_calendar")) && <button className="secondary" onClick={() => setSyncOpen((v) => !v)}>Calendar sync</button>}
            {allows("pipelines") && <Link className="button-link" to="setup">{w("service", true)} &amp; hours</Link>}
            <button onClick={() => setDraft({ day: day0 < today ? today : day0, minute: null, resourceId: filter })} disabled={emptySetup}>New {w.lower("appointment")}</button>
          </>
        }
      />
      {calResult && syncOpen && resultText[calResult] && <p className={`notice small${calResult === "connected" || calResult === "reconnected" ? "" : " warn"}`}>{resultText[calResult]}</p>}
      {syncOpen && <CalendarConnections base={base} onClose={() => setSyncOpen(false)} />}
      {emptySetup && (
        <section className="card notice">
          <b>Set up bookings first.</b> Add your {w.lower("service", true)} and the {w.lower("resource", true)} who provide them{allows("pipelines") ? <> on <Link to="setup">{w("service", true)} &amp; hours</Link></> : " (ask an admin or manager)"}. Then {w.lower("appointment", true)} can be booked here or by your AI agent in chat.
        </section>
      )}
      <div className="cal-toolbar">
        <div className="inline">
          <button className="secondary" onClick={() => setAnchor(addDays(day0, view === "week" ? -7 : -1))} aria-label="Previous">‹</button>
          <button className="secondary" onClick={() => setAnchor(null)}>Today</button>
          <button className="secondary" onClick={() => setAnchor(addDays(day0, view === "week" ? 7 : 1))} aria-label="Next">›</button>
          <b className="cal-title">{title}</b>
        </div>
        <div className="inline">
          <div className="segmented" role="group" aria-label="View">
            <button className={view === "day" ? "" : "secondary"} onClick={() => setView("day")}>Day</button>
            <button className={view === "week" ? "" : "secondary"} onClick={() => setView("week")}>Week</button>
          </div>
        </div>
      </div>
      {resources.length > 1 && (
        <div className="cal-filters" role="group" aria-label="Show">
          <button className={`chip${filter === null ? " on" : ""}`} onClick={() => setFilter(null)}>Everyone</button>
          {resources.map((r) => (
            <button key={r.id} className={`chip${filter === r.id ? " on" : ""}`} onClick={() => setFilter(filter === r.id ? null : r.id)}>
              <span className="dot" style={{ background: r.color }} /> {r.name}
            </button>
          ))}
        </div>
      )}
      <ErrorText error={appts.error} />
      <div className="cal-legend small muted" aria-hidden>
        <span><i className="lg-open" /> Open hours</span>
        <span><i className="lg-off" /> Time off</span>
        {(busyQ.data?.busy.length ?? 0) > 0 && <span><i className="lg-busy" /> Busy in Google or Outlook</span>}
        {allows("bookings") && <span>Drag {w.lower("appointment", true)} to move them</span>}
      </div>
      <ErrorText error={moveError} />
      <div className="cal">
        <div className="cal-head" style={{ gridTemplateColumns: `3.5rem repeat(${days.length}, minmax(0, 1fr))` }}>
          <div />
          {days.map((d) => (
            <button key={d} className={`cal-dayhead${d === today ? " today" : ""}`} onClick={() => { setAnchor(d); setView("day"); }}>
              {fmtDay(d, { weekday: "short" })} <b>{fmtDay(d, { day: "numeric" })}</b>
            </button>
          ))}
        </div>
        <div className="cal-scroll" ref={scroller}>
        <div className="cal-body" style={{ gridTemplateColumns: `3.5rem repeat(${days.length}, minmax(0, 1fr))`, height }}>
          <div className="cal-gutter">
            {hours.map((h) => <span key={h} style={{ top: (h - gridStart) * PX_PER_MIN }}>{minutesLabel(h)}</span>)}
          </div>
          {days.map((d) => {
            const wd = weekdayOf(d);
            const dayStart = startOf(d, tz).getTime();
            const dayEnd = startOf(addDays(d, 1), tz).getTime();
            const offs = s.time_off.filter((o) => (!o.resource_id || !filter || o.resource_id === filter) && new Date(o.starts_at).getTime() < dayEnd && new Date(o.ends_at).getTime() > dayStart);
            const dayAppts = list.filter((a) => dayKey(new Date(a.starts_at), tz) === d);
            return (
              <div
                key={d}
                className="cal-col"
                data-day={d}
                onClick={(e) => {
                  if (emptySetup) return;
                  const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
                  const step = s.settings.slot_interval_minutes;
                  const minute = Math.floor((gridStart + y / PX_PER_MIN) / step) * step;
                  setDraft({ day: d, minute, resourceId: filter });
                }}
              >
                {hours.map((h) => <div key={h} className="cal-hour" style={{ top: (h - gridStart) * PX_PER_MIN }} />)}
                {(filter ? windowsFor(s, wd, filter) : unionWindows(s, wd, resources.map((r) => r.id))).map(([a, b], i) => (
                  <div key={i} className="cal-open" style={{ top: (Math.max(a, gridStart) - gridStart) * PX_PER_MIN, height: (Math.min(b, gridEnd) - Math.max(a, gridStart)) * PX_PER_MIN }} />
                ))}
                {offs.map((o) => {
                  const st = Math.max(new Date(o.starts_at).getTime(), dayStart);
                  const en = Math.min(new Date(o.ends_at).getTime(), dayEnd);
                  const sm = (st - dayStart) / 60000;
                  const em = (en - dayStart) / 60000;
                  const top = Math.max(0, (sm - gridStart) * PX_PER_MIN);
                  const bottom = Math.min(height, (em - gridStart) * PX_PER_MIN);
                  return bottom > top ? (
                    <div key={o.id} className="cal-off" style={{ top, height: bottom - top }} title={o.reason ?? "Time off"}>
                      <small>{o.reason ?? "Time off"}{o.resource_id && !filter ? ` · ${s.resources.find((r) => r.id === o.resource_id)?.name ?? ""}` : ""}</small>
                    </div>
                  ) : null;
                })}
                {(busyQ.data?.busy ?? []).filter((b) => !filter || b.resource_id === filter).map((b, i) => {
                  const st = Math.max(new Date(b.start).getTime(), dayStart);
                  const en = Math.min(new Date(b.end).getTime(), dayEnd);
                  const top = Math.max(0, ((st - dayStart) / 60000 - gridStart) * PX_PER_MIN);
                  const bottom = Math.min(height, ((en - dayStart) / 60000 - gridStart) * PX_PER_MIN);
                  const who = !filter && resources.length > 1 ? s.resources.find((r) => r.id === b.resource_id)?.name : "";
                  const label = `Busy in ${b.provider === "google" ? "Google Calendar" : "Outlook"}${who ? ` · ${who}` : ""}`;
                  return bottom > top ? <div key={`b${i}`} className="cal-busy" style={{ top, height: bottom - top }} title={label}>{bottom - top > 18 && <small>{label}</small>}</div> : null;
                })}
                {d === today && nowMin >= gridStart && nowMin <= gridEnd && <div className="cal-now" style={{ top: (nowMin - gridStart) * PX_PER_MIN }} />}
                {layout(dayAppts, tz, gridStart).map(({ a, top, height: h, lane, lanes }) => (
                  <button
                    key={a.id}
                    className={`cal-appt status-${a.status}${selected === a.id ? " selected" : ""}${drag?.id === a.id ? " dragging" : ""}`}
                    style={{ top, height: h, left: `calc(${(lane / lanes) * 100}% + 2px)`, width: `calc(${100 / lanes}% - 4px)`, borderLeftColor: a.resource_color,
                      ...(drag?.id === a.id && { transform: `translate(${drag.dx}px, ${drag.dy}px)` }) }}
                    onPointerDown={(e) => {
                      if (!allows("bookings") || (a.status !== "booked" && a.status !== "confirmed") || e.button !== 0) return;
                      e.currentTarget.setPointerCapture(e.pointerId);
                      setDrag({ id: a.id, x0: e.clientX, y0: e.clientY, dx: 0, dy: 0 });
                    }}
                    onPointerMove={(e) => { if (drag?.id === a.id) setDrag({ ...drag, dx: e.clientX - drag.x0, dy: e.clientY - drag.y0 }); }}
                    onPointerCancel={() => setDrag(null)}
                    onPointerUp={async (e) => {
                      if (drag?.id !== a.id) return;
                      const moved = Math.abs(drag.dx) > 4 || Math.abs(drag.dy) > 4;
                      setDrag(null);
                      if (!moved) return;
                      e.stopPropagation();
                      justDragged.current = true;
                      setTimeout(() => { justDragged.current = false; }, 0);
                      // Where it was dropped: the day column under the pointer, the time from how far it moved.
                      const col = document.elementsFromPoint(e.clientX, e.clientY).find((el) => (el as HTMLElement).dataset?.day) as HTMLElement | undefined;
                      const dayTo = col?.dataset.day ?? d;
                      const step = s.settings.slot_interval_minutes || 15;
                      const st = wall(new Date(a.starts_at), tz);
                      const minute = Math.max(0, Math.min(24 * 60 - step, Math.round((st.h * 60 + st.min + drag.dy / PX_PER_MIN) / step) * step));
                      if (dayTo === d && minute === st.h * 60 + st.min) return;
                      const [yy, mm, dd] = dayTo.split("-").map(Number);
                      const when = zoned(yy, mm, dd, Math.floor(minute / 60), minute % 60, tz);
                      if (!confirm(`Move ${a.contact_name ?? a.service_name} to ${fmtDay(dayTo, { weekday: "short", day: "numeric", month: "short" })}, ${minutesLabel(minute)}?`)) return;
                      setMoveError(null);
                      const move = (outside: boolean) => api(`${base}/appointments/${a.id}`, { method: "PATCH", json: { starts_at: when.toISOString(), resource_id: a.resource_id, ...(outside && { allow_outside_hours: true }) } });
                      try {
                        try {
                          await move(false);
                        } catch (err) {
                          // Dropped outside working hours: ask, then move it on purpose.
                          if (!(err instanceof ApiError && err.code === "outside_hours")) throw err;
                          if (!confirm(`${minutesLabel(minute)} is outside working hours. Move it there anyway? The customer won't be told it's unusual.`)) return;
                          await move(true);
                        }
                        refresh();
                        toast(`Moved to ${minutesLabel(minute)}`);
                      } catch (err) { setMoveError(err); }
                    }}
                    onClick={(e) => { e.stopPropagation(); if (!justDragged.current) { setSelected(a.id); setDraft(null); } }}
                    title={`${fmtTime(new Date(a.starts_at), tz)} ${a.service_name} · ${a.contact_name ?? ""}`}
                  >
                    <b>{fmtTime(new Date(a.starts_at), tz)} {a.contact_name ?? a.service_name}</b>
                    {h > 34 && <span>{a.service_name}{!filter && resources.length > 1 ? ` · ${a.resource_name}` : ""}</span>}
                    {a.source === "agent" && h > 50 && <span className="ai-tag">Booked by AI</span>}
                  </button>
                ))}
              </div>
            );
          })}
        </div>
        </div>
      </div>
      {sel && <AppointmentPanel a={sel} tz={tz} setup={s} resources={resources} onClose={() => setSelected(null)} onChanged={refresh} />}
      {draft && !sel && (
        <NewAppointment
          tz={tz}
          setup={s}
          services={services}
          resources={resources}
          initial={draft}
          contactId={bookFor}
          onClose={() => { setDraft(null); if (bookFor) { params.delete("contact"); setParams(params, { replace: true }); } }}
          onBooked={(a) => { refresh(); setDraft(null); setSelected(a.id); setAnchor(dayKey(new Date(a.starts_at), tz)); if (bookFor) { params.delete("contact"); setParams(params, { replace: true }); } }}
        />
      )}
    </>
  );
}

function AppointmentPanel({ a, tz, setup, resources, onClose, onChanged }: { a: Appointment; tz: string; setup: BookingSetup; resources: BookingResource[]; onClose: () => void; onChanged: () => void }) {
  const { base, orgId } = useOrg();
  const words = useWords();
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [moving, setMoving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const start = new Date(a.starts_at);
  const active = a.status === "booked" || a.status === "confirmed";
  async function patch(body: Record<string, unknown>) {
    setBusy(true);
    setError(null);
    try {
      await api(`${base}/appointments/${a.id}`, { method: "PATCH", json: body });
      onChanged();
      setMoving(false);
      setCancelling(false);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  const w = wall(start, tz);
  const [date, setDate] = useState(dayKey(start, tz));
  const [time, setTime] = useState(minutesLabel(w.h * 60 + w.min));
  const [res, setRes] = useState(a.resource_id);
  const [outside, setOutside] = useState(false);
  const [pickedOutside, setPickedOutside] = useState(false);
  const [reason, setReason] = useState("");
  useEffect(() => {
    setDate(dayKey(start, tz));
    setTime(minutesLabel(w.h * 60 + w.min));
    setRes(a.resource_id);
    setMoving(false);
    setCancelling(false);
    setError(null);
  }, [a.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <aside className="drawer" aria-label={words("appointment")}>
      <div className="inline" style={{ justifyContent: "space-between" }}>
        <h2 style={{ margin: 0 }}>{a.service_name}</h2>
        <button className="link" onClick={onClose} aria-label="Close">Close</button>
      </div>
      <p className="drawer-when">{fmtDay(dayKey(start, tz), { weekday: "long", day: "numeric", month: "long" })} · {fmtTime(start, tz)}–{fmtTime(new Date(a.ends_at), tz)}</p>
      <span className={`tag appt-status status-${a.status}`}>{statusText[a.status]}</span>
      {a.source === "agent" && <span className="tag ai-tag">Booked by AI</span>}
      <dl className="details">
        <dt>Customer</dt>
        <dd>{a.contact_id ? <Link to={`/o/${orgId}/contacts/${a.contact_id}`}>{a.contact_name ?? "Contact"}</Link> : a.contact_name ?? "—"}{a.contact_phone && <div className="muted small">{a.contact_phone}</div>}</dd>
        <dt>With</dt><dd><span className="dot" style={{ background: a.resource_color }} /> {a.resource_name}</dd>
        {a.price_minor != null && <><dt>Price</dt><dd>{money(a.price_minor, a.currency)}</dd></>}
        {a.deal_id && <><dt>Deal</dt><dd><Link to={`/o/${orgId}/deals/${a.deal_id}`}>Open deal</Link></dd></>}
        {a.thread_id && <><dt>Chat</dt><dd><Link to={`/o/${orgId}/inbox/${a.thread_id}`}>Conversation</Link></dd></>}
        {a.notes && <><dt>Notes</dt><dd style={{ whiteSpace: "pre-wrap" }}>{a.notes}</dd></>}
        {a.cancel_reason && <><dt>Reason</dt><dd>{a.cancel_reason}</dd></>}
        <dt>Reminder</dt><dd>{a.reminder_sent_at ? `Sent ${new Date(a.reminder_sent_at).toLocaleString("en-GB", { timeZone: tz, dateStyle: "medium", timeStyle: "short" })}` : "Not sent yet"}</dd>
      </dl>
      <ErrorText error={error} />
      {active && !moving && !cancelling && (
        <div className="drawer-actions">
          {a.status === "booked" && <button className="secondary" disabled={busy} onClick={() => patch({ status: "confirmed" })}>Confirm</button>}
          <button disabled={busy} onClick={() => patch({ status: "completed" })}>Attended</button>
          <button className="secondary" disabled={busy} onClick={() => patch({ status: "no_show" })}>No-show</button>
          <button className="secondary" disabled={busy} onClick={() => setMoving(true)}>Reschedule</button>
          <button className="secondary danger-text" disabled={busy} onClick={() => setCancelling(true)}>Cancel</button>
        </div>
      )}
      {!active && (
        <div className="drawer-actions">
          <button className="secondary" disabled={busy} onClick={() => patch({ status: "booked" })}>{a.status === "cancelled" ? "Restore booking" : "Undo"}</button>
        </div>
      )}
      {moving && (
        <div className="drawer-form">
          <Field label="Date"><DateInput value={date} onChange={(v) => { setDate(v); setTime(""); }} /></Field>
          {resources.length > 1 && (
            <Field label="With">
              <select value={res} onChange={(e) => { setRes(e.target.value); setTime(""); }}>
                {resources.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </Field>
          )}
          {a.service_id ? (
            <SlotPicker tz={tz} setup={setup} duration={Math.round((new Date(a.ends_at).getTime() - new Date(a.starts_at).getTime()) / 60000)} date={date}
              serviceId={a.service_id} resourceId={res} resourceIds={[res]} resourceName={resources.find((r) => r.id === res)?.name ?? null} exceptId={a.id}
              value={time} onChange={(t, out) => { setTime(t); setPickedOutside(out); }} outside={outside} onOutside={(v) => { setOutside(v); if (!v && pickedOutside) { setTime(""); setPickedOutside(false); } }} />
          ) : (
            <>
              <Field label="Time"><input type="time" step={300} value={time} onChange={(e) => setTime(e.target.value)} /></Field>
              <label className="check"><input type="checkbox" checked={outside} onChange={(e) => { setOutside(e.target.checked); setPickedOutside(e.target.checked); }} /> Allow outside working hours</label>
            </>
          )}
          <div className="drawer-actions">
            <button disabled={busy || !date || !time} onClick={() => {
              const [y, m, d] = date.split("-").map(Number);
              const [hh, mm] = time.split(":").map(Number);
              patch({ starts_at: zoned(y, m, d, hh, mm, tz).toISOString(), resource_id: res, allow_outside_hours: pickedOutside });
            }}>Move {words.lower("appointment")}</button>
            <button className="secondary" onClick={() => setMoving(false)}>Back</button>
          </div>
        </div>
      )}
      {cancelling && (
        <div className="drawer-form">
          <Field label="Reason (optional)"><input value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Customer is travelling" /></Field>
          <div className="drawer-actions">
            <button className="danger" disabled={busy} onClick={() => patch({ status: "cancelled", cancel_reason: reason })}>Cancel {words.lower("appointment")}</button>
            <button className="secondary" onClick={() => setCancelling(false)}>Back</button>
          </div>
        </div>
      )}
      <p className="muted small">Attended marks the deal won; no-show or cancelled marks it lost.</p>
    </aside>
  );
}

function NewAppointment({ tz, setup, services, resources, initial, contactId, onClose, onBooked }: {
  tz: string;
  setup: BookingSetup;
  services: BookingService[];
  resources: BookingResource[];
  initial: { day: string; minute: number | null; resourceId: string | null };
  contactId: string | null;
  onClose: () => void;
  onBooked: (a: Appointment) => void;
}) {
  const w = useWords();
  const { base } = useOrg();
  const [serviceId, setServiceId] = useState(services[0]?.id ?? "");
  const [date, setDate] = useState(initial.day);
  const [time, setTime] = useState(initial.minute != null ? minutesLabel(initial.minute) : "");
  const [resourceId, setResourceId] = useState(initial.resourceId ?? "");
  // A click on the calendar outside working hours starts with those times shown.
  const startsOutside = initial.minute != null && !unionWindows(setup, weekdayOf(initial.day), initial.resourceId ? [initial.resourceId] : resources.map((r) => r.id))
    .some(([a, b]) => initial.minute! >= a && initial.minute! < b);
  const [outside, setOutside] = useState(startsOutside);
  const [pickedOutside, setPickedOutside] = useState(startsOutside);
  const [notes, setNotes] = useState("");
  const [customer, setCustomer] = useState<{ id: string; name: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const svc = services.find((x) => x.id === serviceId);
  const eligible = useMemo(() => resources.filter((r) => !svc || svc.resource_ids.length === 0 || svc.resource_ids.includes(r.id)), [resources, svc]);
  const preset = useQuery({
    queryKey: ["contact-lite", base, contactId],
    queryFn: () => api<Contact>(`${base}/contacts/${contactId}`),
    enabled: !!contactId,
  });
  useEffect(() => {
    if (preset.data) setCustomer({ id: preset.data.id, name: contactName(preset.data) });
  }, [preset.data]);
  async function submit() {
    if (!svc || !date || !time) return;
    setBusy(true);
    setError(null);
    try {
      const [y, m, d] = date.split("-").map(Number);
      const [hh, mm] = time.split(":").map(Number);
      const a = await api<Appointment>(`${base}/appointments`, {
        method: "POST",
        json: { service_id: serviceId, starts_at: zoned(y, m, d, hh, mm, tz).toISOString(), ...(resourceId && { resource_id: resourceId }), ...(customer && { contact_id: customer.id }), notes, allow_outside_hours: pickedOutside },
      });
      onBooked(a);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <aside className="drawer" aria-label={`New ${w.lower("appointment")}`}>
      <div className="inline" style={{ justifyContent: "space-between" }}>
        <h2 style={{ margin: 0 }}>New {w.lower("appointment")}</h2>
        <button className="link" onClick={onClose}>Close</button>
      </div>
      <Field label={w("service")}>
        <select value={serviceId} onChange={(e) => { setServiceId(e.target.value); setResourceId(""); }}>
          {services.map((x) => <option key={x.id} value={x.id}>{x.name} · {x.duration_minutes} min{x.price_minor != null ? ` · ${money(x.price_minor, x.currency)}` : ""}</option>)}
        </select>
      </Field>
      {eligible.length > 1 && (
        <Field label="With">
          <select value={resourceId} onChange={(e) => { setResourceId(e.target.value); setTime(""); setPickedOutside(false); }}>
            <option value="">Anyone available</option>
            {eligible.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </Field>
      )}
      <Field label="Date"><DateInput value={date} onChange={(v) => { setDate(v); setTime(""); setPickedOutside(false); }} /></Field>
      {svc && (
        <SlotPicker tz={tz} setup={setup} duration={svc.duration_minutes} date={date} serviceId={serviceId} resourceId={resourceId}
          resourceIds={resourceId ? [resourceId] : eligible.map((r) => r.id)} resourceName={resources.find((r) => r.id === resourceId)?.name ?? null}
          value={time} onChange={(t, out) => { setTime(t); setPickedOutside(out); }} outside={outside} onOutside={(v) => { setOutside(v); if (!v && pickedOutside) { setTime(""); setPickedOutside(false); } }} />
      )}
      <CustomerPicker value={customer} onChange={setCustomer} />
      <Field label="Notes (optional)"><textarea rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
      <ErrorText error={error} />
      <div className="drawer-actions">
        <button disabled={busy || !svc || !time} onClick={submit}>{busy ? "Booking…" : "Book"}</button>
        <button className="secondary" onClick={onClose}>Cancel</button>
      </div>
      {setup.settings.deal_on_booking && svc?.price_minor != null && <p className="muted small">A {money(svc.price_minor, svc.currency)} deal opens for the customer, and is won when you mark them attended.</p>}
    </aside>
  );
}

/** CustomerPicker finds a contact or creates one on the spot. */
function CustomerPicker({ value, onChange }: { value: { id: string; name: string } | null; onChange: (c: { id: string; name: string } | null) => void }) {
  const { base } = useOrg();
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [first, setFirst] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<unknown>(null);
  const results = useQuery({
    queryKey: ["contact-search", base, q],
    queryFn: () => api<{ contacts: Contact[] }>(`${base}/contacts?q=${encodeURIComponent(q)}&limit=6`),
    enabled: q.trim().length >= 2 && !value,
  });
  if (value) {
    return (
      <div className="field">
        <span>Customer</span>
        <div className="inline"><b>{value.name}</b> <button type="button" className="link small" onClick={() => onChange(null)}>Change</button></div>
      </div>
    );
  }
  if (creating) {
    return (
      <fieldset className="mini-form">
        <legend className="small">New customer</legend>
        <Field label="Name"><input value={first} onChange={(e) => setFirst(e.target.value)} /></Field>
        <Field label="Mobile (with country code)"><input value={phone} placeholder="+973 3300 0000" onChange={(e) => setPhone(e.target.value)} /></Field>
        <Field label="Email (optional)"><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <ErrorText error={error} />
        <div className="inline">
          <button type="button" className="secondary small" disabled={!first.trim()} onClick={async () => {
            setError(null);
            try {
              const c = await api<Contact>(`${base}/contacts`, { method: "POST", json: { first_name: first.trim(), ...(phone.trim() && { phone: phone.trim() }), ...(email.trim() && { email: email.trim() }) } });
              onChange({ id: c.id, name: contactName(c) });
              setCreating(false);
            } catch (err) {
              setError(err);
            }
          }}>Add customer</button>
          <button type="button" className="link small" onClick={() => setCreating(false)}>Back</button>
        </div>
      </fieldset>
    );
  }
  return (
    <div className="field">
      <label className="field">
        <span>Customer (optional)</span>
        <input value={q} placeholder="Search by name, phone or email" onChange={(e) => setQ(e.target.value)} />
      </label>
      {(results.data?.contacts ?? []).length > 0 && (
        <ul className="picker-results">
          {results.data!.contacts.map((c) => (
            <li key={c.id}><button type="button" className="link" onClick={() => onChange({ id: c.id, name: contactName(c) })}>{contactName(c)}</button> <span className="muted small">{c.phone ?? c.email ?? ""}</span></li>
          ))}
        </ul>
      )}
      <div><button type="button" className="link small" onClick={() => { setCreating(true); setFirst(q); }}>+ New customer</button></div>
    </div>
  );
}
